import {Node, ReactFlowInstance} from 'reactflow';

import {assert} from 'Asserts';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  NewTaskDefinitionVersionSpec,
  TaskDefinitionVersionSpec,
  TaskSchemaSpec,
} from 'processes/dao/ProcessFlowchartConfigDAO';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {RouterConfig} from 'processes/modals/router-config/RouterConfigConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {StandardNodeProps} from 'ui-library/reactflow/nodes/BenchlingNode/standard/StandardNode';

import {
  ProcessFlowchartNodeType,
  TaskSchemaNodeSectionType,
  SidepanelTab,
  FieldDefinitionGraphQL,
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
} from './processFlowchartConfigEditorConstants';
import {FlowchartNodeChange} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from './redux/reduceFlowchartConfig';

export type TaskDefinitionUpdater<TApplicationData = unknown> = (
  updater: (
    currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart
  ) => TaskDefinitionVersionSpecWithTaskSchema<TApplicationData>,
  isBlocking: boolean
) => Promise<FlowchartConfigMetadata | null>;

// Task schemas aren't needed for network specs, but it is useful to co-locate with task definition versions
// on the frontend for convenience.
interface NewTaskDefinitionVersionSpecWithTaskSchema<
  TApplicationData = unknown,
> extends NewTaskDefinitionVersionSpec<TApplicationData> {
  taskSchema: TaskSchemaSpec;
}

// This shape will get converted to the network spec shape in the DAO.
export interface TaskDefinitionVersionSpecWithTaskSchema<TApplicationData = unknown> extends Omit<
  TaskDefinitionVersionSpec<TApplicationData>,
  'newProcessTaskDefinitionVersion'
> {
  inputFieldDefinitions: Array<FieldDefinitionGraphQL>;
  outputFieldDefinitions: Array<FieldDefinitionGraphQL> | null;
  newProcessTaskDefinitionVersion: NewTaskDefinitionVersionSpecWithTaskSchema<TApplicationData>;
}

export interface TaskSchemaDomainData {
  id: string;
  nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE;
  nodeDetailsType: ProcessFlowchartNodeDetailsType;
  nodeName: string;
  nodeIcon?: BenchlingIconName;
  nodeSecondaryInfo?: string;
  taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema;
  taskSchemaId: string;
  sections: Array<TaskSchemaNodeSectionData>;
  hideSectionSummaries?: boolean;
  isArchived?: boolean;
  isDisabled?: boolean;
  isEditingNodeName?: boolean;
}

export interface TaskSchemaNodeRowData {
  /** A uuid for the row. */
  id: string;
  /** The client id of the field represented by the row. Should be unique per node. */
  clientId: string;
  isFlowchartTask: boolean;
  sectionName: string;
  sectionType: TaskSchemaNodeSectionType;
  fieldName: string;
  fieldIcon: BenchlingIconName;
  fieldTypeLabel: string | JSX.Element;
  isSelected?: boolean;
  /** Whether this field can be a target of data flow mappings (i.e., receive mapped values). */
  canBeMappedTo: boolean;
  /** Optional status to display on the row. Only 'error' and 'warning' are supported. */
  status?: 'error' | 'warning';
  /** Optional description of the status to display in a tooltip. */
  statusDescription?: string;
  /** Optional icon to display on the right side of the row. */
  secondaryIcon?: BenchlingIconName;
  /** Optional text to display on the right side of the row. */
  secondaryText?: string;
}

export interface TaskSchemaNodeSectionData {
  sectionName: string;
  sectionType: TaskSchemaNodeSectionType;
  sectionRows: Array<TaskSchemaNodeRowData>;
}

export type GetAddNodeOptionsForRightHandle = (
  sourceNodeId: string,
  reactFlowInstance: ReactFlowInstance
) => Array<{
  optionValue: string;
  nodeName: string;
  nodeIcon: BenchlingIconName | null;
  onSelect: () => void;
}> | null;

interface NonTerminalNodeInteractiveData {
  getAddNodeOptionsForRightHandle?: GetAddNodeOptionsForRightHandle;
}

export interface TaskSchemaInteractionNodeData extends NonTerminalNodeInteractiveData {
  onNodeHeaderClick?: () => void;
  onNodeDelete?: () => void;
  nodeDetailsSidepanelContent: Array<SidepanelTab>;
  onFieldClick: (nodeId: string, fieldId: string, rowId: string) => void;
  validationErrors?: Array<ProcessFlowchartValidationError>;
  onUpdateNodeData: (
    nodeChangeGenerator: (flowchartConfig: FlowchartConfigWithMetadata) => Array<FlowchartNodeChange>,
    saveImmediately?: boolean,
    skipSettingDirtyState?: boolean
  ) => Promise<FlowchartConfigMetadata | null>;
}

/**
 * Static data that is used for converting from and persisting task schemas to the database.
 */
export type TaskSchemaStaticNodeData = TaskSchemaDomainData;

/**
 * Populated data to be passed to the base node renderer.
 */
export type TaskSchemaNodeData = TaskSchemaDomainData & TaskSchemaInteractionNodeData;
export type TaskSchemaNode = Node<TaskSchemaNodeData>;

export enum PlaceholderType {
  CREATE_NEW_TASK_SCHEMA = 'CREATE_NEW_TASK_SCHEMA',
  ADD_EXISTING_TASK_SCHEMA = 'ADD_EXISTING_TASK_SCHEMA',
}

interface PlaceholderForTaskSchemaCreateData extends NonTerminalNodeInteractiveData {
  placeholderType: PlaceholderType.CREATE_NEW_TASK_SCHEMA;
  placeholderForNodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE;
  executionType: ProcessExecutionType;
}

interface PlaceholderForAddExistingTaskSchemaData extends NonTerminalNodeInteractiveData {
  placeholderType: PlaceholderType.ADD_EXISTING_TASK_SCHEMA;
  placeholderForNodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE;
}

type PlaceholderForNodeTypeData =
  | PlaceholderForTaskSchemaCreateData
  | PlaceholderForAddExistingTaskSchemaData;

export interface PlaceholderStaticNodeData {
  id: string;
  nodeType: ProcessFlowchartNodeType.PLACEHOLDER_NODE;
  placeholderForNodeTypeData: PlaceholderForNodeTypeData;
  nodeName: string;
  nodeIcon?: BenchlingIconName;
  sections?: Array<TaskSchemaNodeSectionData>;
  hideSectionSummaries?: boolean;
}

export function isPlaceholderForTaskSchemaCreateData(
  nodeData: PlaceholderForNodeTypeData
): nodeData is PlaceholderForTaskSchemaCreateData {
  return nodeData.placeholderType === PlaceholderType.CREATE_NEW_TASK_SCHEMA;
}

export function isPlaceholderForAddExistingTaskSchemaData(
  nodeData: PlaceholderForNodeTypeData
): nodeData is PlaceholderForAddExistingTaskSchemaData {
  return nodeData.placeholderType === PlaceholderType.ADD_EXISTING_TASK_SCHEMA;
}

export interface PlaceholderNodeInteractiveData extends NonTerminalNodeInteractiveData {
  onNodeHeaderClick?: () => void;
  onNodeDelete?: () => void;
  validationErrors?: Array<ProcessFlowchartValidationError>;
}

export type PlaceholderNodeData = StandardNodeProps &
  PlaceholderStaticNodeData &
  PlaceholderNodeInteractiveData;
export type PlaceholderNode = Node<PlaceholderNodeData>;

export interface LogicNodeInteractiveData {
  onNodeHeaderClick?: () => void;
  onNodeDelete?: () => void;
  validationErrors?: Array<ProcessFlowchartValidationError>;
  nodeWidth?: string;
}

export type RouterNodeData = StandardNodeProps &
  RouterStaticNodeData &
  LogicNodeInteractiveData &
  NonTerminalNodeInteractiveData;
export type RouterNode = Node<RouterNodeData>;

export type EndNodeData = StandardNodeProps & EndStaticNodeData & LogicNodeInteractiveData;
export type EndNode = Node<EndNodeData>;

export interface RouterStaticNodeData {
  id: string;
  nodeType: ProcessFlowchartNodeType.ROUTER_NODE;
  nodeDetailsType: ProcessFlowchartNodeDetailsType;
  nodeName: string;
  nodeIcon?: BenchlingIconName;
  routerConfig: RouterConfig;
}

export interface EndStaticNodeData {
  id: string;
  nodeName: string;
  nodeType: ProcessFlowchartNodeType.STOP_NODE;
  nodeDetailsType: ProcessFlowchartNodeDetailsType;
  nodeIcon?: BenchlingIconName;
}

export type StaticFlowchartNodeData =
  | TaskSchemaStaticNodeData
  | RouterStaticNodeData
  | EndStaticNodeData
  | PlaceholderStaticNodeData;

export type InteractiveNodeData = TaskSchemaNodeData | RouterNodeData | EndNodeData | PlaceholderNodeData;

export type FlowchartNode = Node<InteractiveNodeData>;

export function isInteractiveTaskNode(node: FlowchartNode): node is TaskSchemaNode {
  return isStaticTaskSchemaNodeData(node.data);
}

export function isInteractiveRouterNode(node: FlowchartNode): node is RouterNode {
  return isStaticRouterNodeData(node.data);
}

export function isInteractiveEndNode(node: FlowchartNode): node is EndNode {
  return isStaticEndNodeData(node.data);
}

export function isInteractivePlaceholderNode(node: FlowchartNode): node is PlaceholderNode {
  return isStaticPlaceholderNodeData(node.data);
}

/**
 * Helper function to filter an array of flowchart nodes to only interactive task nodes.
 */
export function getInteractiveTaskNodes(nodes: Array<FlowchartNode>): Array<TaskSchemaNode> {
  return nodes.filter((node) => isInteractiveTaskNode(node));
}

/**
 * Helper function to get the data from interactive task nodes.
 */
export function getInteractiveTaskNodeDatas(nodes: Array<FlowchartNode>): Array<TaskSchemaDomainData> {
  return getInteractiveTaskNodes(nodes).map((node) => node.data);
}

export function getStaticTaskNodes(
  nodes: Array<StaticFlowchartNodeData>,
  predicate?: (node: TaskSchemaStaticNodeData) => boolean
): Array<TaskSchemaStaticNodeData> {
  return nodes.filter(
    (node): node is TaskSchemaStaticNodeData =>
      isStaticTaskSchemaNodeData(node) && (predicate?.(node) ?? true)
  );
}

export function isStaticTaskSchemaNodeData(
  nodeData: StaticFlowchartNodeData
): nodeData is TaskSchemaStaticNodeData {
  return nodeData.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE;
}

export function isStaticRouterNodeData(nodeData: StaticFlowchartNodeData): nodeData is RouterStaticNodeData {
  return nodeData.nodeType === ProcessFlowchartNodeType.ROUTER_NODE;
}

export function isStaticEndNodeData(nodeData: StaticFlowchartNodeData): nodeData is EndStaticNodeData {
  return nodeData.nodeType === ProcessFlowchartNodeType.STOP_NODE;
}

export function isStaticPlaceholderNodeData(
  nodeData: StaticFlowchartNodeData
): nodeData is PlaceholderStaticNodeData {
  return nodeData.nodeType === ProcessFlowchartNodeType.PLACEHOLDER_NODE;
}

export function isTaskDefinitionVersionSpecWithTaskSchema<T = unknown>(
  spec: TaskDefinitionVersionSpec<unknown>
): spec is TaskDefinitionVersionSpecWithTaskSchema<T> {
  return (
    spec != null &&
    typeof spec === 'object' &&
    spec.newProcessTaskDefinitionVersion != null &&
    typeof spec.newProcessTaskDefinitionVersion === 'object' &&
    // Check if it has the taskSchema property (indicating it's the WithTaskSchema variant)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (spec.newProcessTaskDefinitionVersion as any).taskSchema != null
  );
}

export function isNodeWithTaskSchema<T = unknown>(
  node: TaskDefinitionVersionSpecWithTaskSchema<unknown>
): node is TaskDefinitionVersionSpecWithTaskSchema<T> {
  return isTaskDefinitionVersionSpecWithTaskSchema<T>(node);
}

export function hasValidApplicationData<T = unknown>(
  node: TaskDefinitionVersionSpecWithTaskSchema<unknown>
): node is TaskDefinitionVersionSpecWithTaskSchema<T> & {
  newProcessTaskDefinitionVersion: {
    applicationData: T;
  };
} {
  return isNodeWithTaskSchema<T>(node) && node.newProcessTaskDefinitionVersion.applicationData != null;
}

/**
 * Safely extracts application data from a flowchart node with runtime validation
 * Throws an error if the node doesn't have the expected structure
 */
export function getApplicationDataSafely<T = unknown>(
  node: TaskDefinitionVersionSpecWithTaskSchema<unknown>
): T {
  assert(hasValidApplicationData<T>(node), 'Node does not have valid application data of the expected type');
  return node.newProcessTaskDefinitionVersion.applicationData;
}

/**
 * Maps a node type to its corresponding full node data types
 */
export interface FlowchartNodeTypeToNodeDataMap {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: TaskSchemaNodeData;
  [ProcessFlowchartNodeType.ROUTER_NODE]: RouterNodeData;
  [ProcessFlowchartNodeType.STOP_NODE]: EndNodeData;
  [ProcessFlowchartNodeType.PLACEHOLDER_NODE]: PlaceholderNodeData;
}

/**
 * Maps static node data types to their corresponding ReactFlow Node types
 */
export interface FlowchartNodeTypeToReactflowNodeMap {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: TaskSchemaNode;
  [ProcessFlowchartNodeType.ROUTER_NODE]: RouterNode;
  [ProcessFlowchartNodeType.STOP_NODE]: EndNode;
  [ProcessFlowchartNodeType.PLACEHOLDER_NODE]: PlaceholderNode;
}

/**
 * Maps node types to their corresponding static data types
 */
export interface NodeTypeToStaticNodeDataMap {
  [ProcessFlowchartNodeType.TASK_SCHEMA_NODE]: TaskSchemaStaticNodeData;
  [ProcessFlowchartNodeType.ROUTER_NODE]: RouterStaticNodeData;
  [ProcessFlowchartNodeType.STOP_NODE]: EndStaticNodeData;
  [ProcessFlowchartNodeType.PLACEHOLDER_NODE]: PlaceholderStaticNodeData;
}

/**
 * Helper type for getting the correct interactive data needed for a specific node type
 */
export type NodeForNodeType<T extends ProcessFlowchartNodeType> = FlowchartNodeTypeToReactflowNodeMap[T];

/**
 * Helper type for getting the static data type for a specific node type
 */
export type StaticDataForNodeType<T extends ProcessFlowchartNodeType> = NodeTypeToStaticNodeDataMap[T];

/**
 * Extract the node type from static data using the discriminant property
 */
export type ExtractNodeType<T extends StaticFlowchartNodeData> = T['nodeType'];

/**
 * Get the corresponding Node type for a given static data type
 */
export type StaticDataToNode<T extends StaticFlowchartNodeData> =
  FlowchartNodeTypeToReactflowNodeMap[ExtractNodeType<T>];
